import React, { useState, useEffect, useRef } from 'react';
import api from '../../../api/axios';
import toast from 'react-hot-toast';
import Select from 'react-select';
import DatasetSelector from '../../Common/DatasetSelector';
import DiscretizationLayout from '../../Common/DiscretizationLayout';
import { Box, Paper, Typography, Button, TextField, MenuItem, Select as MuiSelect, FormControl, InputLabel } from '@mui/material';
import '../../Discretization/ExpertCut/ExpertCut.css';

// Composant Plotly pour Avg by Leaf (with localStorage persistence)
const AvgByLeafPlot = ({ plotData, defaultTitle }) => {
  const plotRef = useRef(null);
  const titleKey = plotData?.titleKey || 'default';
  const [title, setTitle] = useState(() => localStorage.getItem(`cart_avg_title_${titleKey}`) || defaultTitle || '');
  const [xlabel, setXlabel] = useState(() => localStorage.getItem(`cart_avg_xlabel_${titleKey}`) || plotData?.xlabel || '');
  const [ylabel, setYlabel] = useState(() => localStorage.getItem(`cart_avg_ylabel_${titleKey}`) || plotData?.ylabel || '');
  const [showEditor, setShowEditor] = useState(false);
  const renderPlot = (t, xl, yl) => {
    if (!plotData || !plotRef.current || !window.Plotly) return;
    window.Plotly.newPlot(plotRef.current, [{
      x: plotData.x, y: plotData.y, type: 'bar',
      marker: { color: plotData.color || '#3498db' }
    }], {
      title: t, xaxis: { title: xl }, yaxis: { title: yl },
      template: 'plotly_white', height: 350, margin: { t: 50, b: 60, l: 60, r: 20 }
    }, { responsive: true });
  };

  useEffect(() => { renderPlot(title, xlabel, ylabel); }, [plotData, title, xlabel, ylabel]);

  const applyTitles = () => {
    renderPlot(title, xlabel, ylabel);
    localStorage.setItem(`cart_avg_title_${titleKey}`, title);
    localStorage.setItem(`cart_avg_xlabel_${titleKey}`, xlabel);
    localStorage.setItem(`cart_avg_ylabel_${titleKey}`, ylabel);
    setShowEditor(false);
  };

  if (!plotData) return null;
  return (
    <div style={{ marginBottom: '16px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
        <h4 style={{ margin: 0, fontSize: '12px' }}>{title}</h4>
        <button onClick={() => setShowEditor(!showEditor)} style={{ padding: '4px 8px', fontSize: '10px', background: 'none', border: '1px solid #e0e0e0', borderRadius: '4px', cursor: 'pointer' }}>
          {showEditor ? '✕' : '✏️ Edit'}
        </button>
      </div>
      {showEditor && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', padding: '10px', background: '#f8f9fa', borderRadius: '6px', marginBottom: '8px' }}>
          <input type="text" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Title" style={{ padding: '6px', fontSize: '11px', borderRadius: '4px', border: '1px solid #ddd' }} />
          <input type="text" value={xlabel} onChange={(e) => setXlabel(e.target.value)} placeholder="X Axis" style={{ padding: '6px', fontSize: '11px', borderRadius: '4px', border: '1px solid #ddd' }} />
          <input type="text" value={ylabel} onChange={(e) => setYlabel(e.target.value)} placeholder="Y Axis" style={{ padding: '6px', fontSize: '11px', borderRadius: '4px', border: '1px solid #ddd' }} />
          <button onClick={applyTitles} style={{ padding: '6px', fontSize: '11px', background: '#1abc9c', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>Apply</button>
        </div>
      )}
      <div ref={plotRef} style={{ width: '100%', height: '350px' }}></div>
    </div>
  );
};

// Composant Plotly pour Stability (with localStorage persistence)
const StabilityPlot = ({ plotData, defaultTitle }) => {
  const plotRef = useRef(null);
  const titleKey = plotData?.titleKey || 'default';
  const [title, setTitle] = useState(() => localStorage.getItem(`cart_stab_title_${titleKey}`) || defaultTitle || '');
  const [xlabel, setXlabel] = useState(() => localStorage.getItem(`cart_stab_xlabel_${titleKey}`) || plotData?.xlabel || '');
  const [ylabel, setYlabel] = useState(() => localStorage.getItem(`cart_stab_ylabel_${titleKey}`) || plotData?.ylabel || '');
  const [showEditor, setShowEditor] = useState(false);

  const renderPlot = (t, xl, yl) => {
    if (!plotData || !plotRef.current || !window.Plotly) return;
    const traces = plotData.traces.map(tr => ({
      x: plotData.x, y: tr.y, type: 'scatter', mode: 'lines+markers', name: tr.name
    }));
    window.Plotly.newPlot(plotRef.current, traces, {
      title: t, xaxis: { title: xl }, yaxis: { title: yl },
      template: 'plotly_white', height: 400,
      legend: { orientation: 'h', y: -0.2 }, margin: { t: 50, b: 80, l: 60, r: 20 }
    }, { responsive: true });
  };

  useEffect(() => { renderPlot(title, xlabel, ylabel); }, [plotData, title, xlabel, ylabel]);

  const applyTitles = () => {
    renderPlot(title, xlabel, ylabel);
    localStorage.setItem(`cart_stab_title_${titleKey}`, title);
    localStorage.setItem(`cart_stab_xlabel_${titleKey}`, xlabel);
    localStorage.setItem(`cart_stab_ylabel_${titleKey}`, ylabel);
    setShowEditor(false);
  };

  if (!plotData) return null;
  return (
    <div style={{ marginBottom: '16px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
        <h4 style={{ margin: 0, fontSize: '12px' }}>{title}</h4>
        <button onClick={() => setShowEditor(!showEditor)} style={{ padding: '4px 8px', fontSize: '10px', background: 'none', border: '1px solid #e0e0e0', borderRadius: '4px', cursor: 'pointer' }}>
          {showEditor ? '✕' : '✏️ Edit'}
        </button>
      </div>
      {showEditor && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', padding: '10px', background: '#f8f9fa', borderRadius: '6px', marginBottom: '8px' }}>
          <input type="text" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Title" style={{ padding: '6px', fontSize: '11px', borderRadius: '4px', border: '1px solid #ddd' }} />
          <input type="text" value={xlabel} onChange={(e) => setXlabel(e.target.value)} placeholder="X Axis" style={{ padding: '6px', fontSize: '11px', borderRadius: '4px', border: '1px solid #ddd' }} />
          <input type="text" value={ylabel} onChange={(e) => setYlabel(e.target.value)} placeholder="Y Axis" style={{ padding: '6px', fontSize: '11px', borderRadius: '4px', border: '1px solid #ddd' }} />
          <button onClick={applyTitles} style={{ padding: '6px', fontSize: '11px', background: '#1abc9c', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>Apply</button>
        </div>
      )}
      <div ref={plotRef} style={{ width: '100%', height: '400px' }}></div>
    </div>
  );
};

// Composant TestsPanel
const TestsPanel = ({ results }) => {
  const [testType, setTestType] = useState('welch');
  const [split, setSplit] = useState('Train');

  const getData = () => {
    const key = `${testType}_${split.toLowerCase()}`;
    return results[key] || [];
  };

  const data = getData();
  const testNames = { welch: "Welch's t-test", tukey: 'Tukey HSD', games_howell: 'Games-Howell' };

  return (
    <div>
      <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
        <select value={testType} onChange={(e) => setTestType(e.target.value)} style={{ padding: '6px', fontSize: '11px', borderRadius: '4px', border: '1px solid #e0e0e0' }}>
          <option value="welch">Welch</option>
          <option value="tukey">Tukey HSD</option>
          <option value="games_howell">Games-Howell</option>
        </select>
        <select value={split} onChange={(e) => setSplit(e.target.value)} style={{ padding: '6px', fontSize: '11px', borderRadius: '4px', border: '1px solid #e0e0e0' }}>
          <option value="Train">Training</option>
          <option value="Test">Testing</option>
        </select>
      </div>

      <h4 style={{ fontSize: '12px', marginBottom: '8px' }}>{testNames[testType]} - {split}</h4>

      {data.length > 0 ? (
        <div className="table-scroll" style={{ maxHeight: '300px', overflow: 'auto', border: '1px solid #e0e0e0', borderRadius: '6px' }}>
          <table style={{ width: '100%', fontSize: '10px', borderCollapse: 'collapse' }}>
            <thead style={{ position: 'sticky', top: 0, background: '#34495e', color: 'white', zIndex: 10 }}>
              <tr>
                {testType === 'welch' ? (
                  <>
                    <th style={{ padding: '6px' }}>Group 1</th>
                    <th style={{ padding: '6px' }}>Group 2</th>
                    <th style={{ padding: '6px' }}>T</th>
                    <th style={{ padding: '6px' }}>P-Value</th>
                  </>
                ) : testType === 'tukey' ? (
                  <>
                    <th style={{ padding: '6px' }}>Group 1</th>
                    <th style={{ padding: '6px' }}>Group 2</th>
                    <th style={{ padding: '6px' }}>Mean Diff</th>
                    <th style={{ padding: '6px' }}>P-adj</th>
                    <th style={{ padding: '6px' }}>Lower</th>
                    <th style={{ padding: '6px' }}>Upper</th>
                  </>
                ) : (
                  <>
                    <th style={{ padding: '6px' }}>Groups</th>
                    <th style={{ padding: '6px' }}>Mean Diff</th>
                    <th style={{ padding: '6px' }}>Std Err</th>
                    <th style={{ padding: '6px' }}>T</th>
                    <th style={{ padding: '6px' }}>P-Value</th>
                  </>
                )}
              </tr>
            </thead>
            <tbody>
              {data.slice(0, 15).map((r, i) => (
                <tr key={i} style={{ borderBottom: '1px solid #e0e0e0', background: i % 2 === 0 ? 'white' : '#fafafa' }}>
                  {testType === 'welch' ? (
                    <>
                      <td style={{ padding: '4px' }}>{r.Group1}</td>
                      <td style={{ padding: '4px' }}>{r.Group2}</td>
                      <td style={{ padding: '4px' }}>{(typeof r.T === 'number' ? r.T.toFixed(2) : r.T)}</td>
                      <td style={{ padding: '4px' }}>{(typeof r['P-Value'] === 'number' ? r['P-Value'].toFixed(4) : r['P-Value'])}</td>
                    </>
                  ) : testType === 'tukey' ? (
                    <>
                      <td style={{ padding: '4px' }}>{r.group1}</td>
                      <td style={{ padding: '4px' }}>{r.group2}</td>
                      <td style={{ padding: '4px' }}>{(typeof r.meandiff === 'number' ? r.meandiff.toFixed(3) : r.meandiff)}</td>
                      <td style={{ padding: '4px' }}>{(typeof r['p-adj'] === 'number' ? r['p-adj'].toFixed(4) : r['p-adj'])}</td>
                      <td style={{ padding: '4px' }}>{(typeof r.lower === 'number' ? r.lower.toFixed(3) : r.lower)}</td>
                      <td style={{ padding: '4px' }}>{(typeof r.upper === 'number' ? r.upper.toFixed(3) : r.upper)}</td>
                    </>
                  ) : (
                    <>
                      <td style={{ padding: '4px' }}>{r.Groups}</td>
                      <td style={{ padding: '4px' }}>{(typeof r.MeanDiff === 'number' ? r.MeanDiff.toFixed(3) : r.MeanDiff)}</td>
                      <td style={{ padding: '4px' }}>{(typeof r.StdErr === 'number' ? r.StdErr.toFixed(3) : r.StdErr)}</td>
                      <td style={{ padding: '4px' }}>{(typeof r.T === 'number' ? r.T.toFixed(2) : r.T)}</td>
                      <td style={{ padding: '4px' }}>{(typeof r['P-Value'] === 'number' ? r['P-Value'].toFixed(4) : r['P-Value'])}</td>
                    </>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p style={{ fontSize: '11px', color: '#7f8c8d', padding: '10px' }}>No results available</p>
      )}
    </div>
  );
};

const CART = () => {
  const [step, setStep] = useState(0);
  const [columns, setColumns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [datasetInfo, setDatasetInfo] = useState(null);
  const [datasetLoaded, setDatasetLoaded] = useState(false);
  const [showSaveSection, setShowSaveSection] = useState(false);
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  const [activeTab, setActiveTab] = useState('performance');
  const [flagValues, setFlagValues] = useState([]);
  const [loadingFlags, setLoadingFlags] = useState(false);
  const [predictionType, setPredictionType] = useState('leaf');
  const [savingModel, setSavingModel] = useState(false);
  const [modelName, setModelName] = useState('');
  const [aiReport, setAiReport] = useState(null);
  const [generatingReport, setGeneratingReport] = useState(false);
  const [generatingPDF, setGeneratingPDF] = useState(false);
  const [formData, setFormData] = useState({
    Default_Date: null, target_variable: null, predictors: [],
    criterion: 'squared_error', splitter: 'best', max_depth: 5,
    min_samples_split: 2, min_samples_leaf: 1, ccp_alpha: 0.0,
    test_size: 0.3, sampling_flag: null, training_value: null, prediction_col: ''
  });

  // ========== Analysis state variables ==========
  const [savedAnalysisId, setSavedAnalysisId] = useState(null);
  const [analysisStatus, setAnalysisStatus] = useState('draft');
  const [analysisDescription, setAnalysisDescription] = useState('');
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [savingComment, setSavingComment] = useState(false);
  const [isCommentsExpanded, setIsCommentsExpanded] = useState(true);
  const [updatingMetadata, setUpdatingMetadata] = useState(false);
  const [isLoadingAnalysis, setIsLoadingAnalysis] = useState(false);
  const [allDatasets, setAllDatasets] = useState([]);

  // ========== Mode detection ==========
  const mode = new URLSearchParams(window.location.search).get('mode');
  const isViewMode = mode === 'view';
  const isEditMode = mode === 'edit' || !mode;

  const columnOptions = columns.map(c => ({ value: c, label: c }));
  const predictorOptions = columns.filter(c => c !== formData.target_variable?.value).map(c => ({ value: c, label: c }));
  const flagValueOptions = flagValues.map(v => ({ value: v, label: String(v) }));

  useEffect(() => {
    if (columns.length > 0 && formData.predictors.length === 0) {
      const nc = columns.filter(c => !c.includes('Date') && !c.includes('ID') && !c.includes('flag') && !c.includes('sample'));
      setFormData(prev => ({...prev, predictors: nc.slice(0, 5).map(c => ({ value: c, label: c }))}));
    }
    if (!window.Plotly) { const s = document.createElement('script'); s.src = 'https://cdn.plot.ly/plotly-2.27.0.min.js'; document.head.appendChild(s); }
  }, [columns]);

  // ========== Load saved analysis on mount ==========
  useEffect(() => {
    const analysisId = new URLSearchParams(window.location.search).get('analysis_id');
    if (analysisId && !isNaN(parseInt(analysisId))) {
      loadSavedAnalysis(parseInt(analysisId));
    }
  }, []);

  const fetchDatasets = async () => {
    try {
      const projectId = new URLSearchParams(window.location.search).get('project_id');
      if (!projectId) {
        setAllDatasets([]);
        return;
      }
      const res = await api.get(`/api/v1/tables/project/${projectId}/datasets`);
      const datasets = res.data.map(ds => ({
        id: ds.id,
        name: ds.original_filename,
        rows: ds.row_count,
        columns: ds.column_count
      }));
      setAllDatasets(datasets);
    } catch (err) {
      console.error('Failed to load datasets', err);
    }
  };
  useEffect(() => {
    fetchDatasets();
  }, []);

  // ========== hdl with proper dataset info ==========
  const hdl = (data) => {
    setColumns(data.columns || []);
    setDatasetInfo({
      id: data.id,
      name: `Dataset ${data.id}`,
      rows: data.rows,
      columns: data.columns
    });
    setDatasetLoaded(true);
    toast.success(`Loaded ${data.rows?.toLocaleString()} rows`);
  };

  // Update name when allDatasets is loaded
  useEffect(() => {
    if (datasetInfo && allDatasets.length > 0) {
      const datasetFromList = allDatasets.find(d => d.id === datasetInfo.id);
      if (datasetFromList && datasetFromList.name !== datasetInfo.name) {
        setDatasetInfo(prev => ({ ...prev, name: datasetFromList.name }));
      }
    }
  }, [allDatasets, datasetInfo]);

  // ========== Load saved analysis function ==========
  const loadSavedAnalysis = async (analysisId) => {
    setIsLoadingAnalysis(true);
    try {
      const res = await api.get(`/analyses/${analysisId}`);
      const analysis = res.data;
      
      if (analysis.status) setAnalysisStatus(analysis.status);
      if (analysis.description) setAnalysisDescription(analysis.description);
      setSavedAnalysisId(analysis.id);
      if (analysis.comments && Array.isArray(analysis.comments)) setComments(analysis.comments);
      
      if (analysis.input_parameters) {
        if (analysis.input_parameters.dataset_id) {
          setDatasetInfo({
            id: analysis.input_parameters.dataset_id,
            name: analysis.input_parameters.dataset_name,
            rows: analysis.input_parameters.rows,
            columns: analysis.input_parameters.columns
          });
          setDatasetLoaded(true);
          setColumns(analysis.input_parameters.columns || []);
        }
        
        setFormData(prev => ({
          ...prev,
          Default_Date: analysis.input_parameters.Default_Date ? { value: analysis.input_parameters.Default_Date, label: analysis.input_parameters.Default_Date } : null,
          target_variable: analysis.input_parameters.target_variable ? { value: analysis.input_parameters.target_variable, label: analysis.input_parameters.target_variable } : null,
          predictors: (analysis.input_parameters.predictors || []).map(p => ({ value: p, label: p })),
          criterion: analysis.input_parameters.criterion || 'squared_error',
          max_depth: analysis.input_parameters.max_depth || 5,
          min_samples_split: analysis.input_parameters.min_samples_split || 2,
          min_samples_leaf: analysis.input_parameters.min_samples_leaf || 1,
          ccp_alpha: analysis.input_parameters.ccp_alpha || 0,
          test_size: analysis.input_parameters.test_size || 0.3,
          sampling_flag: analysis.input_parameters.sampling_flag ? { value: analysis.input_parameters.sampling_flag, label: analysis.input_parameters.sampling_flag } : null,
          training_value: analysis.input_parameters.training_value ? { value: analysis.input_parameters.training_value, label: analysis.input_parameters.training_value } : null,
          prediction_col: analysis.input_parameters.prediction_col || ''
        }));
        
        if (analysis.input_parameters.results) setResults(analysis.input_parameters.results);
      }
      
      if (analysis.output_result) {
        if (analysis.input_parameters?.ai_report) setAiReport(analysis.input_parameters.ai_report);        setStep(2);
        toast.success('Analysis loaded with results');
      } else if (analysis.input_parameters) {
        setStep(1);
        toast.success('Analysis loaded. Continue to run analysis steps.');
      }
    } catch (err) {
      toast.error('Failed to load analysis');
    } finally {
      setIsLoadingAnalysis(false);
    }
  };

  // ========== Save Analysis function ==========
  const handleSaveAnalysis = async () => {
    if (!datasetInfo) {
      toast.error('No dataset selected');
      return;
    }
    const projectId = new URLSearchParams(window.location.search).get('project_id');
    if (!projectId) {
      toast.error('No project selected');
      return;
    }
    
    setLoading(true);
    try {
      const payload = {
        project_id: parseInt(projectId),
        module_name: 'cart',
        description: analysisDescription,
        input_parameters: {
          dataset_id: datasetInfo.id,
          dataset_name: datasetInfo.name,
          rows: datasetInfo.rows,
          ai_report: aiReport,
          columns: datasetInfo.columns,
          Default_Date: formData.Default_Date?.value || '',
          target_variable: formData.target_variable?.value || '',
          predictors: formData.predictors.map(p => p.value),
          criterion: formData.criterion,
          max_depth: formData.max_depth,
          min_samples_split: formData.min_samples_split,
          min_samples_leaf: formData.min_samples_leaf,
          ccp_alpha: formData.ccp_alpha,
          test_size: formData.test_size,
          sampling_flag: formData.sampling_flag?.value || '',
          training_value: formData.training_value?.value || '',
          prediction_col: formData.prediction_col,
          results: results
        },
        output_result: { ...results, ai_report: aiReport },
        status: analysisStatus,
        comments: comments
      };
      const res = await api.post('/analyses/', payload);
      toast.success(`Analysis saved with ID: ${res.data.id}`);
      setSavedAnalysisId(res.data.id);
    } catch (err) {
      console.error('Save error:', err);
      toast.error('Failed to save analysis');
    } finally {
      setLoading(false);
    }
  };

  // ========== AI Report function ==========
  const handlePDFReport = async () => {
    setGeneratingPDF(true);
    try {
      const res = await api.get('/api/cart/pdf-report', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'cart_analysis_report.pdf';
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Report downloaded');
    } catch (err) {
      toast.error('Failed to generate report');
    } finally {
      setGeneratingPDF(false);
    }
  };

  const handleAIReport = async () => {
    if (!results) {
      toast.error('Run analysis first');
      return;
    }
    setGeneratingReport(true);
    setAiReport(null);
    try {
      const res = await api.post('/api/cart/ai-report');
      setAiReport(res.data.report);
      toast.success('AI report generated');
    } catch (err) {
      toast.error('Failed to generate report. Is Ollama running?');
    } finally {
      setGeneratingReport(false);
    }
  };

  // ========== Update Metadata function ==========
  const handleUpdateMetadata = async () => {
    if (!savedAnalysisId) {
      toast.error('No analysis saved yet');
      return;
    }
    setUpdatingMetadata(true);
    try {
      await api.patch(`/analyses/${savedAnalysisId}`, {
        status: analysisStatus,
        description: analysisDescription
      });
      toast.success('Status and description updated');
    } catch (err) {
      toast.error('Failed to update');
    } finally {
      setUpdatingMetadata(false);
    }
  };

  // ========== Add Comment function ==========
  const handleAddComment = async () => {
    if (!newComment.trim()) return;
    
    const commentObj = {
      id: Date.now(),
      text: newComment,
      created_at: new Date().toISOString(),
      user_id: localStorage.getItem('user_id') || 'user',
      user_email: localStorage.getItem('user_email') || 'Unknown'
    };
    const updatedComments = [...comments, commentObj];
    
    if (savedAnalysisId) {
      setSavingComment(true);
      try {
        await api.patch(`/analyses/${savedAnalysisId}`, { comments: updatedComments });
        setComments(updatedComments);
        setNewComment('');
        toast.success('Comment added');
      } catch (err) {
        toast.error('Failed to add comment');
      } finally {
        setSavingComment(false);
      }
    } else {
      setComments(updatedComments);
      setNewComment('');
      toast.success('Comment added (will be saved when you save the analysis)');
    }
  };

  const loadFlagValues = async (cn) => {
    if (!cn || !datasetInfo?.id) return;
    setLoadingFlags(true);
    try {
      const r = await fetch(`/api/cart/column-values?dataset_id=${datasetInfo.id}&column=${encodeURIComponent(cn)}`);
      const d = await r.json();
      setFlagValues(d.values || []);
    } catch (e) { console.error(e); }
    finally { setLoadingFlags(false); }
  };

  const hfc = (f, v) => { setFormData(p => ({ ...p, [f]: v })); if (f === 'sampling_flag') { setFlagValues([]); setFormData(p => ({ ...p, training_value: null })); if (v?.value) loadFlagValues(v.value); } };
  const selAll = () => setFormData(p => ({ ...p, predictors: predictorOptions }));
  const selNone = () => setFormData(p => ({ ...p, predictors: [] }));

  const bp = () => ({
    Default_Date: formData.Default_Date?.value || '', target_variable: formData.target_variable?.value || '',
    predictors: formData.predictors.map(p => p.value), sampling_flag: formData.sampling_flag?.value || '',
    training_value: formData.training_value?.value || '', criterion: formData.criterion, splitter: formData.splitter,
    max_depth: formData.max_depth || null, min_samples_split: formData.min_samples_split,
    min_samples_leaf: formData.min_samples_leaf, ccp_alpha: formData.ccp_alpha || 0, test_size: formData.test_size || 0.3
  });

  const saveModel = async () => {
    const name = modelName.trim() || `cart_model_${new Date().toISOString().slice(0,19).replace(/:/g, '-')}`;
    setSavingModel(true);
    try {
      const response = await api.post('/api/cart/save-model', { model_name: name });
      toast.success(response.data.message);
      setModelName('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save model');
    } finally {
      setSavingModel(false);
    }
  };

  const s0 = async () => { if (!formData.target_variable || formData.predictors.length === 0) { toast.error('Required fields missing'); return; } setLoading(true); try { await api.post('/api/cart/step0', bp()); toast.success('Saved'); setStep(1); } catch { toast.error('Failed'); } finally { setLoading(false); } };
  const s1 = async () => { setLoading(true); try { const r = await api.post('/api/cart/step1', bp()); setResults(r.data.results); toast.success(r.data.message); setStep(2); } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); } finally { setLoading(false); } };
  const s2 = async () => { setLoading(true); try { await api.post('/api/cart/step2', { new_column_name: formData.prediction_col || (predictionType === 'leaf' ? 'cart_leaf' : 'cart_prediction'), prediction_type: predictionType }); toast.success('Saved!'); setShowSaveSection(false); } catch { toast.error('Failed'); } finally { setLoading(false); } };

  const exp = async () => {
    const customTitles = {
      avg_train_title: localStorage.getItem('cart_avg_title_train') || results?.avg_train_data?.title || '',
      avg_train_xlabel: localStorage.getItem('cart_avg_xlabel_train') || results?.avg_train_data?.xlabel || 'Leaf Node',
      avg_train_ylabel: localStorage.getItem('cart_avg_ylabel_train') || results?.avg_train_data?.ylabel || '',
      avg_test_title: localStorage.getItem('cart_avg_title_test') || results?.avg_test_data?.title || '',
      avg_test_xlabel: localStorage.getItem('cart_avg_xlabel_test') || results?.avg_test_data?.xlabel || 'Leaf Node',
      avg_test_ylabel: localStorage.getItem('cart_avg_ylabel_test') || results?.avg_test_data?.ylabel || '',
      stab_train_title: localStorage.getItem('cart_stab_title_train') || results?.stability_train_data?.title || '',
      stab_train_xlabel: localStorage.getItem('cart_stab_xlabel_train') || results?.stability_train_data?.xlabel || 'Year',
      stab_train_ylabel: localStorage.getItem('cart_stab_ylabel_train') || results?.stability_train_data?.ylabel || '',
      stab_test_title: localStorage.getItem('cart_stab_title_test') || results?.stability_test_data?.title || '',
      stab_test_xlabel: localStorage.getItem('cart_stab_xlabel_test') || results?.stability_test_data?.xlabel || 'Year',
      stab_test_ylabel: localStorage.getItem('cart_stab_ylabel_test') || results?.stability_test_data?.ylabel || '',
    };
    try {
      const response = await api.post('/api/cart/export-with-titles', { custom_titles: customTitles }, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'cart_results.xlsx';
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error('Failed to export results');
    }
  };

  const sc = (t) => { if (t === 0) setStep(0); else if (t === 1 && datasetLoaded) setStep(1); else if (t === 2 && results) setStep(2); };

  const cs = { control: (b) => ({ ...b, minHeight: '34px', height: '34px', borderColor: '#e0e0e0', fontSize: '12px', boxShadow: 'none' }), valueContainer: (b) => ({ ...b, padding: '0 8px' }), placeholder: (b) => ({ ...b, color: '#95a5a6', fontSize: '12px' }), option: (b, s) => ({ ...b, backgroundColor: s.isSelected ? '#1abc9c' : s.isFocused ? '#e8f8f5' : 'white', fontSize: '12px', padding: '8px 12px' }), multiValue: (b) => ({ ...b, backgroundColor: '#e8f8f5', borderRadius: '3px' }), multiValueLabel: (b) => ({ ...b, fontSize: '11px', padding: '2px 6px' }) };
  const tabs = ['Performance', 'Tree', 'Importance', 'Avg by Leaf', 'Stability', 'Tests'];

  return (
    <DiscretizationLayout title="CART" step={step} onStepClick={sc} datasetLoaded={datasetLoaded} results={results}>
      {/* View mode banner */}
      {isViewMode && results && (
        <div style={{ background: '#e8f8f5', padding: '8px 16px', borderRadius: '8px', marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontSize: '13px', color: '#1abc9c' }}>👁️ Viewing saved analysis (read-only)</span>
          <button
            onClick={() => {
              const url = new URL(window.location.href);
              url.searchParams.set('mode', 'edit');
              window.location.href = url.toString();
            }}
            style={{ padding: '4px 12px', fontSize: '12px', background: '#1abc9c', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
          >
            Modify
          </button>
        </div>
      )}

      {step === 0 && (<>
        <DatasetSelector projectId={new URLSearchParams(window.location.search).get("project_id")} apiPrefix="/api/cart" onDatasetLoaded={hdl} />
        
        {datasetInfo && (
          <div style={{ 
            marginTop: '16px', 
            marginBottom: '16px', 
            padding: '12px', 
            background: '#e8f8f5', 
            borderRadius: '8px',
            borderLeft: '4px solid #1abc9c'
          }}>
            <div style={{ fontSize: '13px', color: '#2c3e50', fontWeight: 500 }}>
              📌 Currently selected dataset:
            </div>
            <div style={{ fontSize: '14px', fontWeight: 600, color: '#1abc9c', marginTop: '4px' }}>
              {datasetInfo.name}
            </div>
            <div style={{ fontSize: '12px', color: '#7f8c8d', marginTop: '4px' }}>
              {datasetInfo.rows?.toLocaleString()} rows • {datasetInfo.columns?.length || 0} columns
            </div>
          </div>
        )}

        {columns.length > 0 && (<div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
        <div style={{ display: 'flex', gap: '12px' }}><div style={{ flex: 1 }}><label style={{ fontSize: '11px', fontWeight: 600, color: '#7f8c8d', marginBottom: '4px', display: 'block' }}>Claims Date</label><Select options={columnOptions} value={formData.Default_Date} onChange={(v) => hfc('Default_Date', v)} isClearable placeholder="Select..." styles={cs} isDisabled={isViewMode} /></div><div style={{ flex: 1 }}><label style={{ fontSize: '11px', fontWeight: 600, color: '#e74c3c', marginBottom: '4px', display: 'block' }}>Target *</label><Select options={columnOptions} value={formData.target_variable} onChange={(v) => hfc('target_variable', v)} placeholder="Select..." styles={cs} isDisabled={isViewMode} /></div></div>
        <div style={{ background: '#f0f8ff', padding: '10px', borderRadius: '6px', border: '1px solid #d1ecf1' }}><label style={{ fontSize: '11px', fontWeight: 600, color: '#0c5460', marginBottom: '8px', display: 'block' }}>📊 Train/Test Split</label><div style={{ display: 'flex', gap: '12px' }}><div style={{ flex: 1 }}><label style={{ fontSize: '10px', color: '#7f8c8d', marginBottom: '2px', display: 'block' }}>1. Flag Column</label><Select options={columnOptions} value={formData.sampling_flag} onChange={(v) => hfc('sampling_flag', v)} isClearable placeholder="Select flag..." styles={cs} isDisabled={isViewMode} /></div><div style={{ flex: 1 }}><label style={{ fontSize: '10px', color: '#7f8c8d', marginBottom: '2px', display: 'block' }}>2. Training Value ({flagValues.length})</label><Select key={flagValues.length} options={flagValueOptions} value={formData.training_value} onChange={(v) => hfc('training_value', v)} placeholder={loadingFlags ? 'Loading...' : 'Select...'} styles={cs} isDisabled={!formData.sampling_flag || loadingFlags || isViewMode} /></div></div>{formData.training_value && <div style={{ marginTop: '6px', fontSize: '10px', color: '#155724', background: '#d4edda', padding: '4px 8px', borderRadius: '4px' }}>✅ Training: <strong>{formData.training_value.label}</strong></div>}</div>
        <div><div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}><label style={{ fontSize: '11px', fontWeight: 600, color: '#e74c3c' }}>Predictors *</label><div style={{ display: 'flex', gap: '6px' }}><button type="button" onClick={selAll} style={{ fontSize: '10px', padding: '2px 8px', background: '#ecf0f1', border: '1px solid #bdc3c7', borderRadius: '3px', cursor: 'pointer' }}>All</button><button type="button" onClick={selNone} style={{ fontSize: '10px', padding: '2px 8px', background: '#ecf0f1', border: '1px solid #bdc3c7', borderRadius: '3px', cursor: 'pointer' }}>None</button></div></div><Select options={predictorOptions} value={formData.predictors} onChange={(v) => hfc('predictors', v || [])} isMulti placeholder="Select predictors..." styles={{ ...cs, control: (b) => ({ ...cs.control(b), minHeight: 'auto', height: 'auto' }) }} isDisabled={isViewMode} /></div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}><button type="button" onClick={() => setShowAdvancedOptions(!showAdvancedOptions)} style={{ padding: '6px 12px', fontSize: '11px', background: showAdvancedOptions ? '#1abc9c' : '#f8f9fa', color: showAdvancedOptions ? 'white' : '#7f8c8d', border: '1px solid #e0e0e0', borderRadius: '4px', cursor: 'pointer' }}>{showAdvancedOptions ? '✕ Hide' : '⚙️ Settings'}</button>{!formData.sampling_flag && <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}><label style={{ fontSize: '10px' }}>Test Size:</label><input type="number" step="0.1" value={formData.test_size} onChange={(e) => hfc('test_size', parseFloat(e.target.value))} min="0.1" max="0.5" style={{ width: '60px', padding: '4px', fontSize: '11px', borderRadius: '4px', border: '1px solid #e0e0e0' }} disabled={isViewMode} /></div>}</div>
        {showAdvancedOptions && (<div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '8px', background: '#f8f9fa', padding: '10px', borderRadius: '6px' }}><div><label style={{ fontSize: '10px' }}>Criterion</label><select value={formData.criterion} onChange={(e) => hfc('criterion', e.target.value)} style={{ width: '100%', padding: '4px', fontSize: '11px' }} disabled={isViewMode}><option value="squared_error">MSE</option><option value="friedman_mse">Friedman MSE</option><option value="absolute_error">MAE</option></select></div><div><label style={{ fontSize: '10px' }}>Max Depth</label><input type="number" value={formData.max_depth || ''} onChange={(e) => hfc('max_depth', e.target.value ? parseInt(e.target.value) : null)} placeholder="∞" style={{ width: '100%', padding: '4px', fontSize: '11px' }} disabled={isViewMode} /></div><div><label style={{ fontSize: '10px' }}>Min Samples Split</label><input type="number" value={formData.min_samples_split} onChange={(e) => hfc('min_samples_split', parseInt(e.target.value))} style={{ width: '100%', padding: '4px', fontSize: '11px' }} disabled={isViewMode} /></div><div><label style={{ fontSize: '10px' }}>Min Samples Leaf</label><input type="number" value={formData.min_samples_leaf} onChange={(e) => hfc('min_samples_leaf', parseInt(e.target.value))} style={{ width: '100%', padding: '4px', fontSize: '11px' }} disabled={isViewMode} /></div><div><label style={{ fontSize: '10px' }}>CCP Alpha</label><input type="number" step="0.001" value={formData.ccp_alpha} onChange={(e) => hfc('ccp_alpha', parseFloat(e.target.value))} style={{ width: '100%', padding: '4px', fontSize: '11px' }} disabled={isViewMode} /></div></div>)}
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}><button onClick={s0} disabled={loading || isViewMode} style={{ padding: '8px 24px', fontSize: '13px', background: loading || isViewMode ? '#95a5a6' : '#1abc9c', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>{loading ? 'Saving...' : 'Continue →'}</button></div>
      </div>)}</>)}

      {step === 1 && (<div style={{ padding: '8px 0' }}><div style={{ background: '#f8f9fa', padding: '12px', borderRadius: '6px', marginBottom: '16px', fontSize: '12px' }}><p><strong>Target:</strong> {formData.target_variable?.label}</p><p><strong>Predictors:</strong> {formData.predictors.length} selected</p></div><div style={{ display: 'flex', gap: '8px' }}><button onClick={() => setStep(0)} style={{ padding: '8px 20px', fontSize: '12px', background: '#95a5a6', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>← Back</button><button onClick={s1} disabled={loading || isViewMode} style={{ padding: '8px 24px', fontSize: '12px', background: loading || isViewMode ? '#95a5a6' : '#1abc9c', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>{loading ? 'Running...' : '🚀 Run CART'}</button></div></div>)}

      {step === 2 && results && (<div>
        <div style={{ display: 'flex', gap: '2px', marginBottom: '16px', borderBottom: '1px solid #e0e0e0', paddingBottom: '8px', flexWrap: 'wrap' }}>{tabs.map(t => { const tk = t.toLowerCase().replace(/\s+/g, '-'); return <button key={t} onClick={() => setActiveTab(tk)} style={{ padding: '6px 10px', fontSize: '10px', background: activeTab === tk ? '#1abc9c' : 'transparent', color: activeTab === tk ? 'white' : '#7f8c8d', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>{t}</button>; })}</div>

        {activeTab === 'performance' && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px' }}>
            {[
              { metric: 'RMSE', train: results.performance?.train_rmse, test: results.performance?.test_rmse },
              { metric: 'gAUC', train: results.performance?.train_gauc, test: results.performance?.test_gauc },
              { metric: 'R²', train: results.performance?.train_r2, test: results.performance?.test_r2 }
            ].map((item, i) => (
              <div key={i} style={{ background: 'white', padding: '12px', borderRadius: '8px', textAlign: 'center', border: '1px solid #e0e0e0' }}>
                <div style={{ fontSize: '14px', fontWeight: 600, color: '#2c3e50', marginBottom: '8px' }}>{item.metric}</div>
                <div style={{ display: 'flex', justifyContent: 'space-around', gap: '12px' }}>
                  <div>
                    <div style={{ fontSize: '20px', fontWeight: 700, color: '#3498db' }}>{item.train ?? 'N/A'}</div>
                    <div style={{ fontSize: '10px', color: '#7f8c8d' }}>Training</div>
                  </div>
                  <div>
                    <div style={{ fontSize: '20px', fontWeight: 700, color: '#e74c3c' }}>{item.test ?? 'N/A'}</div>
                    <div style={{ fontSize: '10px', color: '#7f8c8d' }}>Testing</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {activeTab === 'tree' && results.plots?.tree && <img src={`/static/${results.plots.tree}`} alt="Tree" style={{width:'100%',borderRadius:'6px',border:'1px solid #e0e0e0'}} />}

        {activeTab === 'importance' && <div>{results.plots?.importance && <img src={`/static/${results.plots.importance}`} alt="Imp" style={{width:'100%',borderRadius:'6px',border:'1px solid #e0e0e0',marginBottom:'12px'}} />}{results.feature_importance?.length > 0 && <div className="table-scroll" style={{maxHeight:'200px',overflow:'auto',border:'1px solid #e0e0e0',borderRadius:'6px'}}><table style={{width:'100%',borderCollapse:'collapse',fontSize:'11px'}}><thead style={{position:'sticky',top:0,background:'#34495e',color:'white',zIndex:10}}><tr><th style={{padding:'8px'}}>Feature</th><th style={{padding:'8px',textAlign:'right'}}>Importance</th></tr></thead><tbody>{results.feature_importance.map((f,i)=>(<tr key={i}><td style={{padding:'6px'}}>{f.Feature}</td><td style={{padding:'6px',textAlign:'right'}}>{(f.Importance*100).toFixed(2)}%</td></tr>))}</tbody></table></div>}</div>}

        {activeTab === 'avg-by-leaf' && <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {results.avg_train_data ? <AvgByLeafPlot plotData={{...results.avg_train_data, titleKey: 'train'}} defaultTitle={results.avg_train_data?.title} /> : <p style={{ fontSize: '11px', color: '#7f8c8d' }}>No average by leaf data available</p>}
          {results.avg_test_data ? <AvgByLeafPlot plotData={{...results.avg_test_data, titleKey: 'test'}} defaultTitle={results.avg_test_data?.title} /> : null}
        </div>}

        {activeTab === 'stability' && <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {results.stability_train_data ? <StabilityPlot plotData={{...results.stability_train_data, titleKey: 'train'}} defaultTitle={results.stability_train_data?.title} /> : <p style={{ fontSize: '11px', color: '#7f8c8d' }}>No stability data available</p>}
          {results.stability_test_data ? <StabilityPlot plotData={{...results.stability_test_data, titleKey: 'test'}} defaultTitle={results.stability_test_data?.title} /> : null}
        </div>}

        {activeTab === 'tests' && <TestsPanel results={results} />}

        {/* Save Model Section */}
        <div style={{ marginTop: '20px', padding: '15px', background: '#e8f5e9', borderRadius: '6px', border: '1px solid #c8e6c9' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
            <h4 style={{ fontSize: '13px', margin: 0, color: '#2e7d32' }}>💾 Save Trained Model</h4>
            <span style={{ fontSize: '10px', color: '#2e7d32' }}>Save model to apply on other datasets</span>
          </div>
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
            <input
              type="text"
              value={modelName}
              onChange={(e) => setModelName(e.target.value)}
              placeholder="Model name (optional)"
              style={{ flex: 1, padding: '8px', fontSize: '12px', borderRadius: '4px', border: '1px solid #e0e0e0' }}
              disabled={isViewMode}
            />
            <button
              onClick={saveModel}
              disabled={savingModel || isViewMode}
              style={{ padding: '8px 20px', fontSize: '12px', background: savingModel || isViewMode ? '#95a5a6' : '#4caf50', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
            >
              {savingModel ? 'Saving...' : '💾 Save Model'}
            </button>
          </div>
          <p style={{ fontSize: '10px', color: '#2e7d32', marginTop: '8px', marginBottom: 0 }}>
            Saves: Tree structure, encoders, feature names, and all transformations
          </p>
        </div>

        {/* Save Predictions Section */}
        <div style={{marginTop:'20px'}}>
          <button onClick={() => setShowSaveSection(!showSaveSection)} style={{padding:'6px 12px',fontSize:'12px',background:'none',border:'1px solid #e0e0e0',borderRadius:'4px',cursor:'pointer',marginBottom:showSaveSection?'10px':'0'}}>
            {showSaveSection?'✕ Cancel':'💾 Save Predictions to Current Dataset'}
          </button>

          {showSaveSection && (
            <div style={{padding:'12px',background:'#f8f9fa',borderRadius:'6px'}}>
              <div style={{marginBottom:'10px'}}>
                <label style={{fontSize:'11px',fontWeight:600,display:'block',marginBottom:'4px'}}>Prediction Type</label>
                <select
                  value={predictionType}
                  onChange={(e) => setPredictionType(e.target.value)}
                  style={{width:'100%',padding:'6px',fontSize:'11px',borderRadius:'4px',border:'1px solid #e0e0e0'}}
                  disabled={isViewMode}
                >
                  <option value="leaf">Leaf Node (Class/Leaf Number)</option>
                  <option value="value">Predicted Value</option>
                </select>
              </div>

              <div style={{display:'flex',gap:'10px'}}>
                <input
                  type="text"
                  value={formData.prediction_col || (predictionType === 'leaf' ? 'cart_leaf' : 'cart_prediction')}
                  onChange={(e)=>hfc('prediction_col',e.target.value)}
                  placeholder="Column name"
                  style={{flex:1,padding:'8px',fontSize:'12px',borderRadius:'4px',border:'1px solid #e0e0e0'}}
                  disabled={isViewMode}
                />
                <button
                  onClick={s2}
                  disabled={loading || isViewMode}
                  style={{padding:'8px 16px',fontSize:'12px',background: loading || isViewMode ? '#95a5a6' : '#27ae60',color:'white',border:'none',borderRadius:'4px',cursor:'pointer'}}
                >
                  {loading ? 'Saving...' : 'Save to Dataset'}
                </button>
              </div>
            </div>
          )}

          <div style={{display:'flex',gap:'8px',marginTop:'12px'}}>
            <button onClick={()=>setStep(1)} style={{padding:'8px 20px',fontSize:'12px',background:'#95a5a6',color:'white',border:'none',borderRadius:'4px',cursor:'pointer'}}>← Back</button>
            <button onClick={exp} style={{padding:'8px 20px',fontSize:'12px',background:'none',border:'1px solid #3498db',color:'#3498db',borderRadius:'4px',cursor:'pointer'}}>📥 Export Results</button>
          </div>
        </div>

        {/* Comments Section */}
        <div style={{ marginTop: '20px', border: '1px solid #e0e0e0', borderRadius: '8px', overflow: 'hidden' }}>
          <div 
            style={{ 
              background: '#f5f7fa', 
              padding: '12px 16px', 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              cursor: 'pointer',
              userSelect: 'none',
              fontWeight: 600,
              fontSize: '14px',
              color: '#2c3e50'
            }}
            onClick={() => setIsCommentsExpanded(!isCommentsExpanded)}
          >
            <span>💬 Comments & Discussions ({comments.length})</span>
            <span style={{ fontSize: '20px', fontWeight: 'bold' }}>
              {isCommentsExpanded ? '−' : '+'}
            </span>
          </div>
          
          {isCommentsExpanded && (
            <div style={{ padding: '16px', background: 'white' }}>
              <div style={{ maxHeight: '300px', overflow: 'auto', marginBottom: '16px' }}>
                {comments.length === 0 ? (
                  <Typography variant="body2" color="textSecondary" sx={{ textAlign: 'center', py: 2 }}>
                    No comments yet. Be the first to add a comment.
                  </Typography>
                ) : (
                  comments.map((comment) => (
                    <div
                      key={comment.id}
                      style={{
                        padding: '10px 12px',
                        marginBottom: '8px',
                        background: '#f8f9fa',
                        borderRadius: '8px',
                        borderLeft: '3px solid #1abc9c'
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                        <span style={{ fontSize: '11px', fontWeight: 600, color: '#2c3e50' }}>
                          {comment.user_email || comment.user_id || 'Unknown'}
                        </span>
                        <span style={{ fontSize: '10px', color: '#95a5a6' }}>
                          {new Date(comment.created_at).toLocaleString()}
                        </span>
                      </div>
                      <div style={{ fontSize: '13px', color: '#34495e', whiteSpace: 'pre-wrap' }}>
                        {comment.text}
                      </div>
                    </div>
                  ))
                )}
              </div>
              
              <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                <textarea
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  placeholder="Add a comment or question..."
                  style={{
                    flex: 1,
                    padding: '10px',
                    fontSize: '13px',
                    border: '1px solid #ddd',
                    borderRadius: '8px',
                    resize: 'vertical',
                    minHeight: '60px',
                    fontFamily: 'inherit'
                  }}
                />
                <button
                  onClick={handleAddComment}
                  disabled={savingComment || !newComment.trim()}
                  style={{
                    padding: '8px 20px',
                    background: (savingComment || !newComment.trim()) ? '#95a5a6' : '#1abc9c',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: (savingComment || !newComment.trim()) ? 'not-allowed' : 'pointer',
                    fontSize: '13px',
                    height: 'fit-content'
                  }}
                >
                  {savingComment ? 'Sending...' : 'Add Comment'}
                </button>
              </div>
              
              {!savedAnalysisId && comments.length > 0 && (
                <Typography variant="caption" color="textSecondary" sx={{ display: 'block', textAlign: 'center', mt: 1 }}>
                  💡 Comments will be permanently saved when you click "Save Analysis"
                </Typography>
              )}
            </div>
          )}
        </div>

        {/* Professional Action Area */}
        <div style={{ marginTop: '24px', padding: '20px', background: '#f8f9fa', borderRadius: '12px', border: '1px solid #e0e0e0' }}>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '20px' }}>
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Status</InputLabel>
              <MuiSelect
                value={analysisStatus}
                onChange={(e) => setAnalysisStatus(e.target.value)}
                label="Status"
              >
                <MenuItem value="draft">Draft</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="completed">Completed</MenuItem>
                <MenuItem value="validated">Validated</MenuItem>
                <MenuItem value="failed">Failed</MenuItem>
              </MuiSelect>
            </FormControl>
          </div>
          
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Description"
            value={analysisDescription}
            onChange={(e) => setAnalysisDescription(e.target.value)}
            placeholder="Describe the purpose, methodology, or key findings of this CART analysis..."
            sx={{ mb: 3 }}
          />
          
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
            <Button
              variant="outlined"
              onClick={() => setStep(0)}
              disabled={isViewMode}
              size="small"
            >
              New Analysis
            </Button>
            
            <Button
              variant="contained"
              onClick={exp}
              size="small"
              sx={{ bgcolor: '#27ae60', '&:hover': { bgcolor: '#219a52' } }}
            >
              📥 Export
            </Button>
            
            <Button
              variant="contained"
              onClick={handleAIReport}
              disabled={!results || generatingReport || isViewMode}
              size="small"
              sx={{ bgcolor: results ? '#8e44ad' : '#95a5a6', '&:hover': { bgcolor: '#7d3c98' } }}
            >
              🤖 {generatingReport ? 'Generating...' : 'AI Report'}
            </Button>
            <Button
              variant="contained"
              onClick={handlePDFReport}
              disabled={!results || generatingPDF}
              size="small"
              sx={{ bgcolor: results && !generatingPDF ? '#c0392b' : '#95a5a6', '&:hover': { bgcolor: results ? '#a93226' : '#95a5a6' } }}
            >
              📄 {generatingPDF ? 'Generating...' : 'PDF Report'}
            </Button>

            <Button
              variant="contained"
              onClick={handleSaveAnalysis}
              disabled={!results || isViewMode}
              size="small"
              sx={{ bgcolor: results && !isViewMode ? '#1abc9c' : '#95a5a6', '&:hover': { bgcolor: '#16a085' } }}
            >
              💾 Save Analysis
            </Button>
          </Box>

          {/* AI Report Display */}
          {aiReport && (
            <Paper sx={{ mt: 3, p: 2, bgcolor: '#f8f4ff', borderRadius: 2, border: '1px solid #d5c8e8' }}>
              <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1, color: '#6c3483' }}>
                🤖 AI-Generated Report
              </Typography>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.7 }}>
                {aiReport}
              </Typography>
            </Paper>
          )}
        </div>
      </div>)}
    </DiscretizationLayout>
  );
};

export default CART;